import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Star, Quote } from 'lucide-react';

const TestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await axios.get(`${API}/testimonials`);
        setTestimonials(res.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <div data-testid="testimonials-page">
      {/* Hero */}
      <section className="relative py-32 bg-[#4A5D23] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="container-custom relative z-10 text-center">
          <p className="text-[#D4AF37] uppercase tracking-[0.3em] text-sm mb-4">Kind Words</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6">Client Testimonials</h1>
          <p className="text-white/70 max-w-2xl mx-auto text-lg">
            Our greatest joy comes from seeing our clients' visions come to life. Here's what they have to say about their experience with Eden's Dawn.
          </p>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="section-padding bg-[#F5F5F0]">
        <div className="container-custom">
          {testimonials.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#6B7280] text-lg">Testimonials coming soon!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((t, i) => (
                <div
                  key={t.id || i}
                  data-testid={`testimonial-card-${i}`}
                  className="bg-white rounded-2xl shadow-soft p-8 relative group hover:shadow-lg transition-all duration-300"
                >
                  <div className="absolute top-6 right-6">
                    <Quote size={32} className="text-[#E8DFD0] group-hover:text-[#D4AF37] transition-colors" />
                  </div>

                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(t.rating || 5)].map((_, j) => (
                      <Star key={j} size={16} className="fill-[#D4AF37] text-[#D4AF37]" />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="text-[#2C2C2C] leading-relaxed mb-6 italic">
                    "{t.quote || t.content || t.feedback}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-4 pt-4 border-t border-[#E8DFD0]">
                    {t.image_url ? (
                      <img src={t.image_url} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#4A5D23] flex items-center justify-center">
                        <span className="text-white font-heading text-lg">{t.name?.charAt(0)}</span>
                      </div>
                    )}
                    <div>
                      <p className="font-heading text-lg text-[#2C2C2C]">{t.name}</p>
                      <p className="text-sm text-[#D4AF37]">{t.event_type || 'Client'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white text-center">
        <div className="container-custom max-w-2xl">
          <h2 className="font-heading text-3xl md:text-4xl text-[#2C2C2C] mb-4">Ready to Create Your Story?</h2>
          <p className="text-[#6B7280] mb-8">Let us make your special day unforgettable. Reach out to start planning your dream event.</p>
          <Link to="/contact">
            <Button data-testid="testimonials-cta-btn" className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full px-8 py-6 text-lg">
              Start Planning
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage;
